import React, { useState, useEffect } from 'react';
import { Star, Check, ChevronDown, ChevronUp, UtensilsCrossed } from 'lucide-react';
import appConfig from '../config';
import { useAuthFetch } from '../hooks/useAuthFetch';

const StarRating = ({ value, onChange, disabled, size = 28 }) => {
    const [hover, setHover] = useState(0);
    return (
        <div className="flex gap-0.5">
            {[1, 2, 3, 4, 5].map((star) => (
                <button
                    key={star}
                    disabled={disabled}
                    onClick={() => onChange(star)}
                    onMouseEnter={() => setHover(star)}
                    onMouseLeave={() => setHover(0)}
                    className="p-0.5 transition-transform active:scale-90 disabled:cursor-default"
                >
                    <Star
                        size={size}
                        strokeWidth={1.5}
                        className={`transition-colors ${
                            star <= (hover || value)
                                ? 'text-amber-400 fill-amber-400'
                                : 'text-gray-200 fill-gray-100'
                        }`}
                    />
                </button>
            ))}
        </div>
    );
};

const RATING_LABELS = ['', 'Плохо', 'Так себе', 'Нормально', 'Хорошо', 'Отлично'];

const OrderReviewSection = ({ orderId, dishes = [], onDirtyChange }) => {
    const { authFetch } = useAuthFetch();

    const [orderRating, setOrderRating]               = useState(0);
    const [orderComment, setOrderComment]             = useState('');
    const [orderDraft, setOrderDraft]                 = useState('');
    const [orderSaving, setOrderSaving]               = useState(false);
    const [orderCommentSaved, setOrderCommentSaved]   = useState(false);

    const [dishRatings, setDishRatings]               = useState({});
    const [dishComments, setDishComments]             = useState({});
    const [dishDrafts, setDishDrafts]                 = useState({});
    const [dishSaving, setDishSaving]                 = useState({});
    const [dishCommentSaved, setDishCommentSaved]     = useState({});
    const [expandedDish, setExpandedDish]             = useState(null);

    const [loaded, setLoaded]                         = useState(false);

    const isDirty =
        (orderDraft !== orderComment) ||
        Object.keys(dishDrafts).some((id) => (dishDrafts[id] || '') !== (dishComments[id] || ''));

    // Notify parent about dirty state
    useEffect(() => { onDirtyChange?.(isDirty); }, [isDirty]);

    // Block tab close / refresh when unsaved changes exist
    useEffect(() => {
        if (!isDirty) return;
        const handler = (e) => { e.preventDefault(); };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [isDirty]);

    useEffect(() => {
        authFetch(`${appConfig.endpoint}/orders/${orderId}/review`)
            .then((data) => {
                if (data.order_review) {
                    setOrderRating(data.order_review.rating || 0);
                    setOrderComment(data.order_review.comment || '');
                    setOrderDraft(data.order_review.comment || '');
                }
                if (data.dish_reviews?.length) {
                    const ratings = {}, comments = {}, drafts = {};
                    data.dish_reviews.forEach((r) => {
                        ratings[r.dish_id]  = r.rating;
                        comments[r.dish_id] = r.comment || '';
                        drafts[r.dish_id]   = r.comment || '';
                    });
                    setDishRatings(ratings);
                    setDishComments(comments);
                    setDishDrafts(drafts);
                }
            })
            .catch(() => {})
            .finally(() => setLoaded(true));
    }, [orderId]);

    const saveOrderRating = async (rating) => {
        setOrderRating(rating);
        setOrderSaving(true);
        try {
            await authFetch(`${appConfig.endpoint}/orders/${orderId}/review`, {
                method: 'POST',
                body: JSON.stringify({ rating, comment: orderComment || null }),
            });
        } catch {}
        finally { setOrderSaving(false); }
    };

    const saveOrderComment = async () => {
        setOrderSaving(true);
        try {
            await authFetch(`${appConfig.endpoint}/orders/${orderId}/review`, {
                method: 'POST',
                body: JSON.stringify({ rating: orderRating, comment: orderDraft || null }),
            });
            setOrderComment(orderDraft);
            setOrderCommentSaved(true);
            setTimeout(() => setOrderCommentSaved(false), 2500);
        } catch {}
        finally { setOrderSaving(false); }
    };

    const saveDishRating = async (dishId, rating) => {
        setDishRatings((prev) => ({ ...prev, [dishId]: rating }));
        setDishSaving((prev) => ({ ...prev, [dishId]: true }));
        try {
            await authFetch(`${appConfig.endpoint}/orders/${orderId}/dish-review`, {
                method: 'POST',
                body: JSON.stringify({
                    dish_id: dishId,
                    rating,
                    comment: dishComments[dishId] || null,
                }),
            });
        } catch {}
        finally { setDishSaving((prev) => ({ ...prev, [dishId]: false })); }
    };

    const saveDishComment = async (dishId) => {
        setDishSaving((prev) => ({ ...prev, [dishId]: true }));
        const draft = dishDrafts[dishId] || '';
        try {
            await authFetch(`${appConfig.endpoint}/orders/${orderId}/dish-review`, {
                method: 'POST',
                body: JSON.stringify({
                    dish_id: dishId,
                    rating: dishRatings[dishId],
                    comment: draft || null,
                }),
            });
            setDishComments((prev) => ({ ...prev, [dishId]: draft }));
            setDishCommentSaved((prev) => ({ ...prev, [dishId]: true }));
            setTimeout(() => setDishCommentSaved((prev) => ({ ...prev, [dishId]: false })), 2500);
        } catch {}
        finally { setDishSaving((prev) => ({ ...prev, [dishId]: false })); }
    };

    if (!loaded) return null;

    const dishCommentChanged = (dishId) => (dishDrafts[dishId] || '') !== (dishComments[dishId] || '');

    return (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            {/* Header */}
            <div className="flex items-center gap-2 mb-4">
                <Star size={15} className="text-amber-400 fill-amber-400" />
                <h2 className="text-sm font-semibold text-gray-700">Оценить заказ</h2>
            </div>

            {/* Overall rating */}
            <p className="text-xs text-gray-400 mb-2 font-medium">Общая оценка</p>
            <div className="flex items-center gap-3">
                <StarRating value={orderRating} onChange={saveOrderRating} disabled={orderSaving} size={30} />
                {orderRating > 0 && (
                    <span className="text-sm font-medium text-gray-500">{RATING_LABELS[orderRating]}</span>
                )}
            </div>

            {/* Order comment */}
            {orderRating > 0 && (
                <div className="mt-3">
                    <textarea
                        value={orderDraft}
                        onChange={(e) => setOrderDraft(e.target.value)}
                        placeholder="Расскажите о вашем визите..."
                        rows={3}
                        className="w-full text-sm text-gray-700 bg-gray-50 border border-gray-100 rounded-xl px-3 py-2.5 resize-none focus:outline-none focus:border-brandBlue/40 placeholder:text-gray-300 transition-colors"
                    />
                    <button
                        onClick={saveOrderComment}
                        disabled={orderSaving || orderDraft === orderComment}
                        className="mt-1.5 flex items-center gap-1.5 text-xs font-medium disabled:opacity-40 active:scale-95 transition-all"
                    >
                        {orderCommentSaved ? (
                            <>
                                <Check size={13} className="text-green-500" />
                                <span className="text-green-500">Сохранено</span>
                            </>
                        ) : (
                            <span className={orderDraft === orderComment ? 'text-gray-300' : 'text-brandBlue'}>
                                Сохранить комментарий
                            </span>
                        )}
                    </button>
                </div>
            )}

            {/* Dish ratings */}
            {dishes.length > 0 && orderRating > 0 && (
                <div className="mt-5 pt-4 border-t border-gray-50">
                    <p className="text-xs text-gray-400 font-medium mb-3">Оцените блюда по желанию</p>
                    <div className="space-y-4">
                        {dishes.map((dish) => {
                            const isExpanded = expandedDish === dish.id;
                            const rated      = dishRatings[dish.id] > 0;

                            return (
                                <div key={dish.id} className="pb-4 border-b border-gray-50 last:border-0 last:pb-0">
                                    {/* Dish row */}
                                    <div className="flex items-center gap-3 mb-2.5">
                                        {dish.image ? (
                                            <img
                                                src={dish.image}
                                                alt={dish.title}
                                                className="w-10 h-10 rounded-lg object-cover shrink-0"
                                            />
                                        ) : (
                                            <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                                                <UtensilsCrossed size={16} className="text-gray-300" />
                                            </div>
                                        )}
                                        <p className="text-sm font-medium text-gray-800 flex-1 min-w-0 truncate">
                                            {dish.title}
                                        </p>
                                    </div>

                                    <StarRating
                                        value={dishRatings[dish.id] || 0}
                                        onChange={(r) => saveDishRating(dish.id, r)}
                                        disabled={dishSaving[dish.id]}
                                        size={24}
                                    />

                                    {/* Comment toggle */}
                                    {rated && (
                                        <button
                                            onClick={() => setExpandedDish(isExpanded ? null : dish.id)}
                                            className="mt-2 flex items-center gap-1 text-xs text-gray-400 active:scale-95 transition-all"
                                        >
                                            {isExpanded ? (
                                                <><ChevronUp size={13} /> Скрыть отзыв</>
                                            ) : (
                                                <>
                                                    <ChevronDown size={13} />
                                                    {dishComments[dish.id] ? 'Редактировать отзыв' : 'Написать отзыв'}
                                                </>
                                            )}
                                        </button>
                                    )}

                                    {/* Comment textarea */}
                                    {rated && isExpanded && (
                                        <div className="mt-2.5">
                                            <textarea
                                                value={dishDrafts[dish.id] || ''}
                                                onChange={(e) =>
                                                    setDishDrafts((prev) => ({ ...prev, [dish.id]: e.target.value }))
                                                }
                                                placeholder="Ваш отзыв о блюде..."
                                                rows={2}
                                                className="w-full text-sm text-gray-700 bg-gray-50 border border-gray-100 rounded-xl px-3 py-2.5 resize-none focus:outline-none focus:border-brandBlue/40 placeholder:text-gray-300 transition-colors"
                                            />
                                            <button
                                                onClick={() => saveDishComment(dish.id)}
                                                disabled={dishSaving[dish.id] || !dishCommentChanged(dish.id)}
                                                className="mt-1.5 flex items-center gap-1.5 text-xs font-medium disabled:opacity-40 active:scale-95 transition-all"
                                            >
                                                {dishCommentSaved[dish.id] ? (
                                                    <>
                                                        <Check size={13} className="text-green-500" />
                                                        <span className="text-green-500">Сохранено</span>
                                                    </>
                                                ) : (
                                                    <span className={!dishCommentChanged(dish.id) ? 'text-gray-300' : 'text-brandBlue'}>
                                                        Сохранить
                                                    </span>
                                                )}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default OrderReviewSection;